'use client'

import { useEffect, useRef, useState, useCallback } from 'react'
import {
  Document as FileText,
  CheckmarkOutline as CheckCircle,
  Link as LinkIcon,
  Globe,
  MisuseOutline as Ban,
  WarningAlt as AlertTriangle,
  Security as Shield,
  Scale,
  Renew as RefreshCw,
  Email as Mail,
  ChevronRight,
  ChevronDown,
  Close as X,
} from '@carbon/icons-react'

import { TermsSections } from '@/components/terms/TermsSections'

// ── Config ────────────────────────────────────────────────────────────────
const LAST_UPDATED = 'February 28, 2026'
const EFFECTIVE_DATE = 'February 28, 2026'
const CONTACT_EMAIL = 'hq@euclideum.com'

/**
 * Height (px) of your site's fixed top navbar.
 * The mobile TOC bar is rendered directly below it.
 */
const NAV_HEIGHT = 72
/** Height (px) of the mobile TOC bar itself. Used to offset scroll targets. */
const MOBILE_TOC_H = 49

const sections = [
  {
    id: 'interpretation',
    label: 'Interpretation & Definitions',
    icon: FileText,
  },
  { id: 'acknowledgment', label: 'Acknowledgment', icon: CheckCircle },
  { id: 'links', label: 'Links to Other Websites', icon: LinkIcon },
  { id: 'social-media', label: 'Third-Party Social Media', icon: Globe },
  { id: 'termination', label: 'Termination', icon: Ban },
  { id: 'liability', label: 'Limitation of Liability', icon: AlertTriangle },
  { id: 'disclaimer', label: '"AS IS" & "AS AVAILABLE"', icon: Shield },
  { id: 'governing-law', label: 'Governing Law', icon: Scale },
  { id: 'disputes', label: 'Disputes Resolution', icon: Scale },
  { id: 'severability', label: 'Severability & Waiver', icon: RefreshCw },
  { id: 'translation', label: 'Translation Interpretation', icon: Globe },
  { id: 'changes', label: 'Changes to Terms', icon: RefreshCw },
  { id: 'contact', label: 'Contact Us', icon: Mail },
]

// ── Helpers ───────────────────────────────────────────────────────────────

/**
 * Scrolls to a section instantly so the browser never overshoots.
 * Accounts for both the navbar and the fixed mobile TOC bar.
 */
function jumpToSection(id: string) {
  const el = document.getElementById(id)
  if (!el) return
  const top =
    el.getBoundingClientRect().top +
    window.scrollY -
    NAV_HEIGHT -
    MOBILE_TOC_H -
    12
  window.scrollTo({ top: Math.max(0, top), behavior: 'instant' })
}

// ── Active-section tracker ────────────────────────────────────────────────

function useActiveSection(): string {
  const [activeId, setActiveId] = useState<string>(sections[0]?.id ?? '')

  const detect = useCallback(() => {
    const hash = window.location.hash.slice(1)
    if (hash && sections.some((s) => s.id === hash)) {
      setActiveId(hash)
      return
    }
    const threshold = window.scrollY + NAV_HEIGHT + MOBILE_TOC_H + 24
    let found = sections[0]?.id ?? ''
    for (const { id } of sections) {
      const el = document.getElementById(id)
      if (el && el.offsetTop <= threshold) found = id
    }
    setActiveId(found)
  }, [])

  useEffect(() => {
    detect()
    window.addEventListener('hashchange', detect)
    window.addEventListener('scroll', detect, { passive: true })
    return () => {
      window.removeEventListener('hashchange', detect)
      window.removeEventListener('scroll', detect)
    }
  }, [detect])

  return activeId
}

// ── Mobile TOC FIXED below the navbar (< lg) ───────────────────────────

function MobileTOC({ activeId }: { activeId: string }) {
  const [open, setOpen] = useState(false)
  const wrapRef = useRef<HTMLDivElement>(null)

  const activeSection = sections.find((s) => s.id === activeId)
  const ActiveIcon = activeSection?.icon ?? FileText
  const activeIndex = Math.max(
    0,
    sections.findIndex((s) => s.id === activeId)
  )

  const close = useCallback(() => setOpen(false), [])

  // Close on outside click
  useEffect(() => {
    if (!open) return
    const onPointer = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node))
        close()
    }
    document.addEventListener('mousedown', onPointer)
    return () => document.removeEventListener('mousedown', onPointer)
  }, [open, close])

  // Close on Escape
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, close])

  // Lock body scroll while panel is open
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : ''
    return () => {
      document.body.style.overflow = ''
    }
  }, [open])

  return (
    <div
      ref={wrapRef}
      className="lg:hidden fixed left-0 right-0 z-40 bg-card border-b border-border shadow-sm"
      style={{ top: `${NAV_HEIGHT}px` }}
    >
      {/* ── Trigger button ─────────────────────────────────────────── */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-controls="mobile-toc-panel"
        aria-label="Toggle table of contents"
        className="
          w-full flex items-center gap-3
          px-4 py-3 sm:px-5
          text-left
          focus-visible:outline-none focus-visible:ring-2
          focus-visible:ring-inset focus-visible:ring-emerald-600 dark:focus-visible:ring-primary-active
        "
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <ActiveIcon
            className="size-4 text-emerald-600 dark:text-primary-active flex-shrink-0"
            aria-hidden="true"
          />
          <span className="text-sm font-medium text-foreground/90 truncate leading-none">
            {activeSection?.label ?? 'Table of Contents'}
          </span>
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <span className="text-xs text-muted-foreground/80 font-mono tabular-nums">
            {activeIndex + 1}/{sections.length}
          </span>
          {open ? (
            <X className="size-4 text-muted-foreground/80" aria-hidden="true" />
          ) : (
            <ChevronDown
              className="size-4 text-muted-foreground/80"
              aria-hidden="true"
            />
          )}
        </div>
      </button>

      {/* ── Section-progress underline ─────────────────────────────── */}
      <div
        aria-hidden="true"
        className="absolute bottom-0 left-0 right-0 h-[2px] bg-muted"
      >
        <div
          className="h-full bg-emerald-600 dark:bg-primary-active transition-[width] duration-300 ease-out"
          style={{ width: `${((activeIndex + 1) / sections.length) * 100}%` }}
        />
      </div>

      {/* ── Dropdown panel ─────────────────────────────────────────── */}
      {open && (
        <>
          {/* Backdrop */}
          <div
            aria-hidden="true"
            className="fixed inset-0 bg-black/20 backdrop-blur-[2px] z-40"
            style={{ top: `${NAV_HEIGHT + MOBILE_TOC_H}px` }}
            onClick={close}
          />

          <nav
            id="mobile-toc-panel"
            role="dialog"
            aria-label="Table of contents"
            className="
              absolute top-full left-0 right-0 z-50
              bg-card border-b border-border shadow-xl
              max-h-[70vh] overflow-y-auto overscroll-contain
            "
          >
            <ul className="p-2 space-y-px" role="list">
              {sections.map(({ id, label, icon: Icon }) => {
                const active = activeId === id
                return (
                  <li key={id}>
                    <a
                      href={`#${id}`}
                      aria-current={active ? 'location' : undefined}
                      onClick={(e) => {
                        e.preventDefault()
                        close()
                        jumpToSection(id)
                      }}
                      className={[
                        'relative flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors',
                        active
                          ? 'bg-emerald-600/10 dark:bg-primary-active/10 text-foreground font-semibold'
                          : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground active:bg-muted',
                      ].join(' ')}
                    >
                      {active && (
                        <span
                          aria-hidden="true"
                          className="absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-r-full bg-emerald-600 dark:bg-primary-active"
                        />
                      )}
                      <Icon
                        className={`size-4 flex-shrink-0 ${active ? 'text-emerald-600 dark:text-primary-active' : 'text-muted-foreground/80'}`}
                        aria-hidden="true"
                      />
                      <span className="flex-1 leading-snug">{label}</span>
                      {active && (
                        <ChevronRight
                          className="size-3.5 text-emerald-600 dark:text-primary-active flex-shrink-0"
                          aria-hidden="true"
                        />
                      )}
                    </a>
                  </li>
                )
              })}
            </ul>

            {/* Contact card */}
            <div className="m-2 mt-0 rounded-xl border border-border bg-muted/60 p-4">
              <p className="text-sm font-semibold text-foreground/90 mb-0.5">
                Questions about these terms?
              </p>
              <p className="text-xs text-muted-foreground mb-2.5">
                We reply within 1–2 business days.
              </p>
              <a
                href={`mailto:${CONTACT_EMAIL}`}
                className="
                  inline-flex items-center gap-1.5
                  text-sm font-semibold text-emerald-600 dark:text-primary-active
                  hover:underline
                  focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600 dark:focus-visible:ring-primary-active rounded
                "
              >
                <Mail className="size-3.5" aria-hidden="true" />
                {CONTACT_EMAIL}
              </a>
            </div>
          </nav>
        </>
      )}
    </div>
  )
}

// ── Page ─────────────────────────────────────────────────────────────────

export default function TermsOfServicePage() {
  const activeId = useActiveSection()

  return (
    <main className="relative bg-background min-h-screen">
      {/* ── Hero ─────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden hero-dark-bg text-foreground">
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.07]"
          style={{
            background:
              'linear-gradient(135deg, hsl(var(--primary-active)) 0%, transparent 60%)',
          }}
        />
        <div className="relative z-10 w-full max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-24">
          <div className="max-w-3xl space-y-5 sm:space-y-6">
            <h1 className="font-heading text-3xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-tight">
              Terms of Service
            </h1>
            <p className="text-base sm:text-lg lg:text-xl text-muted-foreground leading-relaxed max-w-2xl">
              Please read these terms carefully. They govern your access to and
              use of our website and services.
            </p>
            <div className="flex flex-wrap gap-x-4 gap-y-1.5 text-sm sm:text-base text-muted-foreground">
              <span>
                Last updated:{' '}
                <strong className="text-foreground">{LAST_UPDATED}</strong>
              </span>
              <span className="hidden sm:inline">·</span>
              <span>
                Effective:{' '}
                <strong className="text-foreground">{EFFECTIVE_DATE}</strong>
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* ── Mobile TOC (fixed bar, always visible while scrolling) ───── */}
      <MobileTOC activeId={activeId} />

      {/* ── Main content ─────────────────────────────────────────────── */}
      {/* pt-[49px] on mobile clears the fixed TOC bar; lg resets it */}
      <div className="bg-card pt-[49px] lg:pt-0">
        <div className="w-full max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-12 lg:py-20">
          <div className="grid gap-10 lg:grid-cols-[260px_1fr] lg:gap-20">
            {/* ── Desktop sidebar TOC ──────────────────────────────── */}
            <aside className="hidden lg:block" aria-label="Page navigation">
              <div
                className="sticky space-y-8"
                style={{ top: `${NAV_HEIGHT + 20}px` }}
              >
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-4 px-2">
                    Table of Contents
                  </p>
                  <div className="space-y-0.5">
                    {sections.map(({ id, label, icon: Icon }) => {
                      const active = activeId === id
                      return (
                        <a
                          key={id}
                          href={`#${id}`}
                          aria-current={active ? 'location' : undefined}
                          onClick={(e) => {
                            e.preventDefault()
                            jumpToSection(id)
                          }}
                          className={[
                            'group relative flex items-center gap-3 rounded-lg px-4 py-2.5',
                            'text-sm transition-all duration-200',
                            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600 dark:focus-visible:ring-primary-active',
                            active
                              ? 'bg-emerald-600/10 dark:bg-primary-active/10 text-foreground font-semibold'
                              : 'text-muted-foreground hover:bg-muted/50 hover:text-foreground/90',
                          ].join(' ')}
                        >
                          <span
                            aria-hidden="true"
                            className={[
                              'absolute left-0 top-1 bottom-1 w-[3px] rounded-r-full bg-emerald-600 dark:bg-primary-active transition-all duration-200',
                              active
                                ? 'opacity-100 scale-y-100'
                                : 'opacity-0 scale-y-75 group-hover:opacity-30 group-hover:scale-y-90',
                            ].join(' ')}
                          />
                          <Icon
                            className={[
                              'size-4 flex-shrink-0 transition-colors duration-150',
                              active
                                ? 'text-emerald-600 dark:text-primary-active'
                                : 'text-muted-foreground/80 group-hover:text-emerald-600/80 dark:group-hover:text-primary-active/80',
                            ].join(' ')}
                            aria-hidden="true"
                          />
                          <span className="flex-1">{label}</span>
                          <ChevronRight
                            aria-hidden="true"
                            className={[
                              'size-4 transition-all duration-200',
                              active
                                ? 'translate-x-0.5 opacity-100 text-emerald-600 dark:text-primary-active'
                                : 'translate-x-0 opacity-0 group-hover:translate-x-0.5 group-hover:opacity-60 text-muted-foreground/80',
                            ].join(' ')}
                          />
                        </a>
                      )
                    })}
                  </div>
                </div>

                {/* Contact card */}
                <div className="rounded-xl border border-border bg-muted/50 p-6 space-y-3 text-sm">
                  <p className="font-semibold text-foreground/90">
                    Questions about these terms?
                  </p>
                  <p className="text-muted-foreground leading-relaxed">
                    Contact our team we usually reply within 1–2 business days.
                  </p>
                  <a
                    href={`mailto:${CONTACT_EMAIL}`}
                    className="
                      inline-flex items-center gap-2 font-semibold text-emerald-600 dark:text-primary-active
                      hover:underline
                      focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600 dark:focus-visible:ring-primary-active rounded
                    "
                  >
                    <Mail className="size-4" aria-hidden="true" />
                    {CONTACT_EMAIL}
                  </a>
                </div>
              </div>
            </aside>

            {/* ── Content ──────────────────────────────────────────── */}
            <div className="min-w-0 max-w-3xl pb-16 sm:pb-20">
              <TermsSections />
            </div>
          </div>
        </div>
      </div>
    </main>
  )
}
